// import { openDB } from 'idb';
// import type { DBSchema, IDBPDatabase } from 'idb';
import { writable, derived } from "svelte/store"
import { acim } from "@utils/acim"
import { isNavigable } from "@circleofa/acim-engine"
import { isRenderable } from "@circleofa/acim-engine"
import { generateIcon } from "@components/material/Icon.module"
import { awaitStore } from "@utils/helpers/await-store"
import { intervalToDuration } from "date-fns"

///
//  HTML AUDIO INIT
//

// Media type support
// Strange workaround because iPhone returns false for audio/caf. So if both are false, it defaults back to caf.
export const AUDIO_FORMAT_EXTENSION = new Audio().canPlayType("audio/caf").length
    ? "caf"
    : new Audio().canPlayType("audio/webm")
      ? "webm"
      : "caf"
console.log("🎶 Media Format Detected: ", AUDIO_FORMAT_EXTENSION)

function stringToURL(path: string) {
    return `https://audio.acimce.app/${path}.${AUDIO_FORMAT_EXTENSION}`
}

export const openAudio = writable(true)
export interface AudioInfo {
    title: string
    prefix: string
}
export const audioItem = writable<AudioInfo | null>(null)
export const audioButtonVisible = derived([openAudio, audioItem], ([openAudio, audioItem]) => {
    return !openAudio && audioItem // audio popup is closed and nothing is playing
})

export function stop() {
    audioElement.pause()
    openAudio.set(false)
    audioItem.set(null)
}

export const audioElement = new Audio()
audioElement.setAttribute("preload", "none")
document.body.appendChild(audioElement)

export const volumeChangeable = writable(false)
async function isVolumeChangeable() {
    const lastSetting = audioElement.volume
    audioElement.volume = 0.9
    await new Promise((r) => setTimeout(r, 0))
    const changed = audioElement.volume == 0.9 // Supposed to equal one in iOS
    audioElement.volume = lastSetting // Change it back
    volumeChangeable.set(changed)
}
isVolumeChangeable()

export const checking = writable(false)

// @ts-expect-error audio
window.audioElement = audioElement

/**
 * Plays the audio file of a given reference
 * @param reference e.g. T-2.II
 */
export async function play(reference: string) {
    await awaitStore(checking, (l) => !l) // Hold if loading
    // Check if path is real
    const found = isPlayable(reference)
    if (!found) {
        audioItem.set(null)
        throw new Error("not playable")
    }

    // Check if it exists on server
    const url = stringToURL(reference)
    playFile(url, {
        title: found.path,
        prefix: found.label,
    })
}

/**
 * Sets the audio element's source and plays it
 * @param path Source file path
 * @param info Information about the audio file
 */
export async function playFile(path: string, info: AudioInfo) {
    checking.set(true)
    audioElement.pause()
    audioElement.src = path
    audioItem.set(info)
    try {
        await audioElement.play()
    } catch (e) {
        console.warn(e)
    } finally {
        openAudio.set(true)
        checking.set(false)
    }
}

// export function connectAudio(node : HTMLElement) {
//   node.appendChild(audioElement)
//   audioElement.controls = true
//   return {
//     destroy() {
//       audioElement.controls = false
//       document.body.appendChild(audioElement)
//     }
//   }
// }

export function isPlayable(path: string) {
    const found = acim.find(path)
    const rtn = found && isNavigable(found) && isRenderable(found) ? found : null
    // Below should be the guard that reflects what's available on the server as in *.caf or webm
    if (
        path.startsWith("W-") ||
        path.startsWith("C-") ||
        path.startsWith("M-") ||
        path.startsWith("T-") ||
        path.startsWith("In")
    )
        return rtn
    return false
}

const speaker = generateIcon("speaker")

type SetupAudioOnNodeOptions = {
    path: string
    onActivate?: () => void
}

export function setupAudioOnNode(node: HTMLElement, options: string | SetupAudioOnNodeOptions) {
    const path = typeof options === "string" ? options : options.path
    const onActivate = typeof options === "string" ? undefined : options.onActivate
    const title =
        node.querySelector(".chaptertitle")?.firstElementChild ||
        node.querySelector(".title")?.firstElementChild ||
        node.querySelector(".firstPrinciple")?.firstElementChild
    if (!title) return

    let el: HTMLAnchorElement
    if (isPlayable(path)) {
        el = document.createElement("a")
        el.classList.add("speaker-button")
        el.href = "#"
        el.addEventListener("click", (e) => {
            e.preventDefault()
            onActivate?.()
            void play(path)
        })
        el.innerHTML = speaker
        title[title.parentElement?.classList.contains("firstPrinciple") ? "append" : "prepend"](el)
    }

    return {
        destroy() {
            if (el) el.remove()
        },
    }
}

// @ts-expect-error audio
window.isPlayable = isPlayable

// @ts-expect-error audio
window.stopAudio = stop

// @ts-expect-error audio
window.play = play

// @ts-expect-error audio
window.openAudio = openAudio

function formatSeconds(seconds: number) {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
    // { minutes: 30, seconds: 7 }

    return `${duration.minutes}:${(duration.seconds + "").padStart(2, "0")}`
}

// External stores:
function setupMediaStores(audioElement: HTMLAudioElement) {
    const duration = writable("0:00")
    const durationChange = () => duration.set(formatSeconds(audioElement.duration))

    const time = writable("0:00")
    const ended = writable(false)
    const percentage = writable(0)
    const timeChange = () => {
        time.set(formatSeconds(audioElement.currentTime))
        ended.set(!!audioElement.duration && audioElement.duration == audioElement.currentTime)
        percentage.set(audioElement.duration ? audioElement.currentTime / audioElement.duration : 0)
    }

    const muted = writable(false)
    const volume = writable(1)
    const volumeChange = () => {
        muted.set(audioElement.muted)
        volume.set(audioElement.volume)
    }

    const playbackRate = writable(1)
    const rateChange = () => playbackRate.set(audioElement.playbackRate)

    const playing = writable(false)
    const isPlaying = () => playing.set(true)
    const isPaused = () => playing.set(false)

    const loading = writable(true)
    const emptied = () => {
        loading.set(true)
        error.set(null)
    }
    const loadstart = () => loading.set(true)
    const canplay = () => loading.set(false)

    const waiting = writable(false)
    const playingAfterWaiting = () => waiting.set(false)
    const waitingForNetwork = () => waiting.set(true)

    const buffering = writable(true)
    const suspend = () => buffering.set(false)
    const progress = () => buffering.set(true)

    const error = writable<typeof audioElement.error>(null)
    const onError = () => error.set(audioElement.error)

    function launch() {
        audioElement.addEventListener("durationchange", durationChange)
        audioElement.addEventListener("timeupdate", timeChange)
        audioElement.addEventListener("volumechange", volumeChange)
        audioElement.addEventListener("ratechange", rateChange)
        audioElement.addEventListener("play", isPlaying)
        audioElement.addEventListener("pause", isPaused)
        audioElement.addEventListener("emptied", emptied)
        audioElement.addEventListener("loadstart", loadstart)
        audioElement.addEventListener("canplay", canplay)
        audioElement.addEventListener("playing", playingAfterWaiting)
        audioElement.addEventListener("waiting", waitingForNetwork)
        audioElement.addEventListener("abort", onError)
        audioElement.addEventListener("error", onError)
        audioElement.addEventListener("suspend", suspend)
        audioElement.addEventListener("progress", progress)
    }

    function destroy() {
        audioElement.removeEventListener("durationchange", durationChange)
        audioElement.removeEventListener("timeupdate", timeChange)
        audioElement.removeEventListener("volumechange", volumeChange)
        audioElement.removeEventListener("ratechange", rateChange)
        audioElement.removeEventListener("play", isPlaying)
        audioElement.removeEventListener("pause", isPaused)
        audioElement.removeEventListener("emptied", emptied)
        audioElement.removeEventListener("loadstart", loadstart)
        audioElement.removeEventListener("canplay", canplay)
        audioElement.removeEventListener("playing", playingAfterWaiting)
        audioElement.removeEventListener("waiting", waitingForNetwork)
        audioElement.removeEventListener("abort", onError)
        audioElement.removeEventListener("error", onError)
        audioElement.removeEventListener("suspend", suspend)
        audioElement.removeEventListener("progress", progress)
    }

    launch()
    return {
        duration,
        percentage,
        time,
        ended,
        muted,
        volume,
        playbackRate,
        playing,
        loading,
        waiting,
        buffering,
        error,
        destroy,
    }
}

export const {
    duration,
    percentage,
    time,
    ended,
    muted,
    volume,
    playbackRate,
    playing,
    loading,
    waiting,
    buffering,
    error,
    destroy,
} = setupMediaStores(audioElement)
error.subscribe((e) => (e ? console.warn("Audobook error: ", e) : ""))
